import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Autocomplete,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import ErrorDisplay from '~/components/Common/ErrorDisplay';
import { formatAffiliationOptionLabel } from '~/components/LeAgentHub/formatAffiliationOptionLabel';

import { useGetAffiliationList } from '~/hooks/apis/agent/useGetAffiliationList';

import { Affiliation, Agency } from '~/types/services/agentHub';

const SUPPORTED_COUNTRIES = [
  { country: 'Australia', label: 'Australia', regionCode: 'AU', aliases: ['Australia'] },
  { country: 'Canada', label: 'Canada', regionCode: 'CA', aliases: ['Canada'] },
  { country: 'Hong Kong', label: 'Hong Kong', regionCode: 'HK', aliases: ['Hong Kong', 'HK', 'Hong Kong SAR'] },
  { country: 'Ireland', label: 'Ireland', regionCode: 'IE', aliases: ['Ireland', 'Republic of Ireland'] },
  { country: 'New Zealand', label: 'New Zealand', regionCode: 'NZ', aliases: ['New Zealand'] },
  { country: 'Singapore', label: 'Singapore', regionCode: 'SG', aliases: ['Singapore'] },
  {
    country: 'United States',
    label: 'USA',
    regionCode: 'US',
    aliases: ['USA', 'United States', 'United States of America'],
  },
  { country: 'United Kingdom', label: 'UK', regionCode: 'GB', aliases: ['UK', 'United Kingdom', 'Great Britain'] },
] as const;

function getSupportedCountry(country?: string) {
  const lower = (country ?? '').toLowerCase();
  return SUPPORTED_COUNTRIES.find((option) => option.aliases.some((alias: string) => alias.toLowerCase() === lower));
}

function normaliseAgencyCountry(agency: Agency): Agency {
  const supportedCountry = getSupportedCountry(agency.country);

  if (!supportedCountry) {
    return agency;
  }

  return {
    ...agency,
    country: supportedCountry.country,
  };
}

export interface AgencyEditorProps {
  data: Agency;
  onSave: (agencyData: Agency) => void;
  onCancel: () => void;
  loadingState: Utils.FetchingState;
  onSelectedAffiliationChange?: (affiliation: Affiliation | null) => void;
}

export default function AgencyEditor({
  data,
  onSave,
  onCancel,
  loadingState,
  onSelectedAffiliationChange,
}: AgencyEditorProps) {
  const [editedData, setEditedData] = useState<Agency>(() => normaliseAgencyCountry(data));

  useEffect(() => {
    setEditedData(normaliseAgencyCountry(data));
  }, [data]);

  const selectedCountry = useMemo(() => getSupportedCountry(editedData.country), [editedData.country]);

  const {
    data: availableAffiliations = [],
    isLoading: isLoadingAffiliations,
    isSuccess: hasLoadedAffiliations,
  } = useGetAffiliationList(selectedCountry?.regionCode, {
    enabled: !!selectedCountry,
    includeWorld: false,
  });

  useEffect(() => {
    if (!editedData.affiliationId) {
      onSelectedAffiliationChange?.(null);
      return;
    }

    const affiliation = availableAffiliations.find((a) => a.id === editedData.affiliationId) ?? null;

    if (!affiliation && hasLoadedAffiliations) {
      setEditedData((currentData) =>
        currentData.affiliationId === editedData.affiliationId
          ? {
              ...currentData,
              affiliationId: undefined,
              affiliation: '',
            }
          : currentData,
      );
      onSelectedAffiliationChange?.(null);
      return;
    }

    onSelectedAffiliationChange?.(affiliation);
  }, [editedData.affiliationId, availableAffiliations, hasLoadedAffiliations, onSelectedAffiliationChange]);

  const handleAgencyUpdate = useCallback(async () => {
    onSave(editedData);
  }, [editedData, onSave]);

  const handleCountryChange = useCallback((event: SelectChangeEvent<string>) => {
    setEditedData((prev) => ({
      ...prev,
      country: event.target.value,
      affiliationId: undefined,
      affiliation: '',
    }));
  }, []);

  const selectedAffiliation = useMemo(() => {
    const list = availableAffiliations ?? [];
    const byId = list.find((a) => a.id === editedData.affiliationId);
    if (byId) {
      return byId;
    }
    if (editedData.affiliation) {
      return list.find((a) => a.name === editedData.affiliation) ?? null;
    }
    return null;
  }, [availableAffiliations, editedData.affiliation, editedData.affiliationId]);

  return (
    <>
      <form onSubmit={(e) => e.preventDefault()} style={{ minWidth: '500px' }}>
        <Stack mt={2} direction="column" spacing={2}>
          <FormGroup>
            <TextField
              label="Name"
              value={editedData.name}
              required
              fullWidth
              onChange={(e) => setEditedData({ ...editedData, name: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <TextField
              label={editedData.businessCodeType}
              value={editedData.businessCodeValue}
              required
              fullWidth
              onChange={(e) => setEditedData({ ...editedData, businessCodeValue: e.target.value })}
            />
          </FormGroup>
          <Typography mt={2} variant="h6" sx={{ gridColumn: 'span 2' }}>
            Address
          </Typography>

          <FormGroup>
            <TextField
              label="Street"
              value={editedData.address}
              required
              fullWidth
              onChange={(e) => setEditedData({ ...editedData, address: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <TextField
              label="City"
              value={editedData.city}
              required
              fullWidth
              onChange={(e) => setEditedData({ ...editedData, city: e.target.value })}
            />
          </FormGroup>

          <FormGroup>
            <TextField
              label="Postal Code"
              value={editedData.postcode}
              required
              fullWidth
              onChange={(e) => setEditedData({ ...editedData, postcode: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <TextField
              label="State"
              value={editedData.state}
              required
              fullWidth
              onChange={(e) => setEditedData({ ...editedData, state: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <FormControl fullWidth required>
              <InputLabel>Country</InputLabel>
              <Select label="Country" value={selectedCountry?.country ?? ''} onChange={handleCountryChange}>
                {SUPPORTED_COUNTRIES.map((country) => (
                  <MenuItem key={country.regionCode} value={country.country}>
                    {country.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FormGroup>
          <Typography mt={2} variant="h6" sx={{ gridColumn: 'span 2' }}>
            Affiliation
          </Typography>
          <FormGroup>
            <Autocomplete
              value={selectedAffiliation}
              options={availableAffiliations}
              getOptionLabel={formatAffiliationOptionLabel}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={isLoadingAffiliations}
              onChange={(_, value) =>
                setEditedData({
                  ...editedData,
                  affiliationId: value?.id,
                  affiliation: value?.name ?? '',
                })
              }
              renderInput={(params) => <TextField {...params} label="Affiliation" required />}
              disabled={!selectedCountry}
            />
          </FormGroup>
          <Typography mt={2} variant="h6" sx={{ gridColumn: 'span 2' }}>
            Features
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editedData.isAgentRecordsEnabled ?? false}
                  onChange={(e) =>
                    setEditedData({
                      ...editedData,
                      isAgentRecordsEnabled: e.target.checked,
                    })
                  }
                />
              }
              label="Enable agent records"
            />
          </FormGroup>
          {loadingState == 'failed' && (
            <ErrorDisplay message="There was an error updating the agency" severity="error" />
          )}
        </Stack>
      </form>
      <Stack mt={2} display="flex" justifyContent="space-around" direction="row">
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={loadingState === 'loading'} variant="contained" onClick={handleAgencyUpdate}>
          {loadingState === 'loading' ? 'Saving...' : 'Save'}
        </Button>
      </Stack>
    </>
  );
}
